/**
 * Per-user role leveling — `users/{uid}/role_progress/{roleId}`.
 * Same EXP curve as snippets ({@link LevelingSystem#calculateExpToLevel}), but no level cap.
 */

import { collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import LevelingSystem from '../components/SRS/LevelingSystem';
import { loadPublishedRoles } from './rolesService';
import { resolveContributingRoleIdsForTrack } from '../utils/roleTrackContribution';

/** EXP granted per successful cookbook study rep (matches snippet card EXP). */
export const ROLE_EXP_PER_REP = 10;

const levelingSystem = new LevelingSystem(null);

/**
 * @param {string} roleId
 * @returns {{ roleId: string, level: number, current_exp: number, exp_to_level: number }}
 */
export function createDefaultRoleProgress(roleId) {
  return {
    roleId,
    level: 1,
    current_exp: 0,
    exp_to_level: levelingSystem.calculateExpToLevel(2)
  };
}

/**
 * @param {string} roleId
 * @param {Record<string, unknown>|null|undefined} data
 * @returns {{ roleId: string, level: number, current_exp: number, exp_to_level: number }}
 */
export function mapRoleProgressDocument(roleId, data) {
  if (!roleId) {
    return createDefaultRoleProgress('unknown');
  }
  if (!data || typeof data !== 'object') {
    return createDefaultRoleProgress(roleId);
  }

  let level = Math.round(Number(data.level) || 1);
  level = Math.max(level, 1);

  const current_exp = Math.max(0, Math.round(Number(data.current_exp) || 0));

  const exp_to_level =
    data.exp_to_level != null
      ? Math.max(1, Math.round(Number(data.exp_to_level) || 0))
      : levelingSystem.calculateExpToLevel(level + 1);

  return { roleId, level, current_exp, exp_to_level };
}

/**
 * Apply EXP with level-ups for roles (uncapped — unlike snippet {@link applySnippetExpGain}).
 * @param {LevelingSystem} levelingSystem - Instance with calculateExpToLevel
 * @param {number} level - Current level
 * @param {number} currentExp - Current EXP toward next level
 * @param {number|undefined|null} expToLevelMaybe - Threshold to complete current level bar; derived if missing
 * @param {number} expGain - EXP to add (e.g. 10)
 * @returns {{ level: number, current_exp: number, exp_to_level: number, leveledUp: boolean }}
 */
export function applyRoleExpGain(levelingSystem, level, currentExp, expToLevelMaybe, expGain) {
  let lvl = Math.max(level || 1, 1);
  let cur = currentExp || 0;
  let need =
    expToLevelMaybe == null ? levelingSystem.calculateExpToLevel(lvl + 1) : expToLevelMaybe;

  let leveledUp = false;
  cur += expGain;
  while (cur >= need) {
    lvl++;
    cur -= need;
    leveledUp = true;
    need = levelingSystem.calculateExpToLevel(lvl + 1);
  }
  return { level: lvl, current_exp: cur, exp_to_level: need, leveledUp };
}

/**
 * Load role progress for the given role ids. Missing docs default to Level 1.
 * @param {string|null|undefined} userId
 * @param {string[]} roleIds
 * @returns {Promise<Map<string, { roleId: string, level: number, current_exp: number, exp_to_level: number }>>}
 */
export async function loadRoleProgressForRoles(userId, roleIds) {
  const ids = [...new Set((roleIds || []).filter(Boolean))];
  /** @type {Map<string, ReturnType<typeof createDefaultRoleProgress>>} */
  const map = new Map();
  ids.forEach((id) => map.set(id, createDefaultRoleProgress(id)));

  if (!userId || ids.length === 0) {
    return map;
  }

  try {
    const snap = await getDocs(collection(db, 'users', userId, 'role_progress'));
    snap.forEach((docSnap) => {
      if (map.has(docSnap.id)) {
        map.set(docSnap.id, mapRoleProgressDocument(docSnap.id, docSnap.data()));
      }
    });
  } catch (e) {
    console.error('roleProgressService: loadRoleProgressForRoles failed', e);
  }

  return map;
}

/**
 * Award role EXP after a successful cookbook study rep.
 * Resolves contributing roles from `trackId`, applies uncapped leveling, persists to Firestore.
 * @param {string|null|undefined} userId
 * @param {string|null|undefined} trackId
 * @param {number} [expAmount=ROLE_EXP_PER_REP]
 * @param {string|null|undefined} [nodeId]
 * @returns {Promise<{ roleIds: string[], updates: Array<{ roleId: string, level: number, current_exp: number, exp_to_level: number, leveledUp: boolean }> }>}
 */
export async function awardRoleExpForStudy(userId, trackId, expAmount = ROLE_EXP_PER_REP, nodeId = null) {
  const uid = userId != null ? String(userId).trim() : '';
  const tid = trackId != null ? String(trackId).trim() : '';
  if (!uid || !tid || !Number.isFinite(expAmount) || expAmount <= 0) {
    return { roleIds: [], updates: [] };
  }

  const roles = await loadPublishedRoles();
  const roleIds = resolveContributingRoleIdsForTrack(tid, roles, nodeId);
  if (!roleIds.length) {
    return { roleIds: [], updates: [] };
  }

  const progressMap = await loadRoleProgressForRoles(uid, roleIds);
  /** @type {Array<{ roleId: string, level: number, current_exp: number, exp_to_level: number, leveledUp: boolean }>} */
  const updates = [];

  await Promise.all(
    roleIds.map(async (roleId) => {
      const current = progressMap.get(roleId) ?? createDefaultRoleProgress(roleId);
      const gain = applyRoleExpGain(
        levelingSystem,
        current.level,
        current.current_exp,
        current.exp_to_level,
        expAmount
      );

      const ref = doc(db, 'users', uid, 'role_progress', roleId);
      await setDoc(
        ref,
        {
          level: gain.level,
          current_exp: gain.current_exp,
          exp_to_level: gain.exp_to_level,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );

      const roleMeta = roles.find((r) => r.roleId === roleId);
      updates.push({
        roleId,
        roleTitle: roleMeta?.title ?? roleId,
        roleIconClass: roleMeta?.iconClass ?? 'ti-user',
        expAmount,
        ...gain
      });
    })
  );

  return { roleIds, updates };
}
